import { Component, OnInit } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { GridReadyEvent, ICellRendererParams, RowDataUpdatedEvent } from 'ag-grid-community';
import { ColDef, ColumnApi, GridApi, GridOptions, ValueGetterParams } from 'ag-grid-enterprise';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import moment from 'moment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CxoneHighlightSearchCellRendererComponent } from 'src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlayComponent } from '../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { DataFeedActionsCellRendererComponent } from './actions-cell/data-feed-actions-cell-renderer.component';
import { CxoneEtlDataFeedDependenciesComponent } from './cxone-etl-data-feed-dependencies/cxone-etl-data-feed-dependencies.component';
import { DataList, ETLDataFeedDependencies, ETLDataFeedObj, PERMISSION } from './cxone-etl-data-feed.model';
import { CxoneEtlDataFeedService } from './cxone-etl-data-feed.service';
import { EtlCustomCellValueRendererComponent } from '../shared/etl-custom-cell-value-renderer/etl-custom-cell-value-renderer.component';
import { IconDetail, IconType } from '../shared/etl-custom-cell-value-renderer/etl-custom-cell-value-renderer.model';
import { LockIconCellRendererComponent } from '../cxone-manage-metrics/ag-grid-components/lock-icon-cell-renderer.component';

@Component({
    selector: 'cv-cxone-etl-data-feed',
    templateUrl: './cxone-etl-data-feed.component.html',
    styleUrls: ['./cxone-etl-data-feed.component.scss'],
    providers: [DialogService]
})
export class CxoneEtlDataFeedComponent implements OnInit {
    public dataFeedLabel: string;
    public dataFeedGridHeaders: Array<ColDef>;
    public dataFeedGridData: ETLDataFeedObj[] = [];
    public filteredDataFeedGridData: ETLDataFeedObj[] = [];
    public context: ThisType<CxoneEtlDataFeedComponent>;
    public searchInput = '';
    public searchPlaceholderText = '';
    public dataFeedGridOptions: GridOptions;
    public gridApi: GridApi;
    public showDataFeedLandingPage = true;
    public showDataFeedViewPage = false;
    public selectedDataFeed: ETLDataFeedObj;
    public showFailedImports: boolean;
    public apiType: string;
    public pageTitle: string;
    public isLocalizationSectionLoaded: boolean;
    public showSpinner: boolean;
    public isEditable = false;
    public isPreview = false;
    public showCreateNewDataFeedPage = false;
    private dialogRef: DynamicDialogRef;
    public permissions: string[] = [];
    public showFailedImportBtn = true;
    public showNewDataFeedBtn = true;
    public showEditBtn = true;
    public showViewBtn = true;
    public showReactivateBtn = true;
    public showDeleteBtn = true;
    public showPreviewBtn = false;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataFeedService: CxoneEtlDataFeedService,
        private toastrManager: ToastrManager,
        private _dialogService: DialogService
    ) { }

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
        this.setGridOptions();
        this.permissions = await this.cxoneEtlDataFeedService.getPermissions("CXOneETLDataFeed") || [];
        this.toggleElementPermissions();
    }

    toggleElementPermissions() {
        if (this.permissions.includes(PERMISSION.VIEW) && !this.permissions.includes(PERMISSION.CREATE)) {
            this.showFailedImportBtn = true;
            this.showNewDataFeedBtn = false;
            this.showEditBtn = false;
            this.showReactivateBtn = false;
            this.showDeleteBtn = false;
            this.showPreviewBtn = true;
        } else if (!this.permissions.includes(PERMISSION.VIEW) && !this.permissions.includes(PERMISSION.CREATE)) {
            this.showFailedImportBtn = false;
            this.showNewDataFeedBtn = false;
            this.showEditBtn = false;
            this.showViewBtn = false;
            this.showReactivateBtn = false;
            this.showDeleteBtn = false;
            this.showPreviewBtn = false;
        }
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Data_Feed');
        }
        catch {
            console.log('Failed to load localization for ETL_Data_Feed');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search_Data_Feeds", "Search data feeds");
        }
    }

    public async loadDataFeed() {
        const promises: [
            Promise<ETLDataFeedObj[]>,
            Promise<ETLDataFeedObj[]>,
        ] = [
            this.cxoneEtlDataFeedService.loadDataFeed(),
            this.cxoneEtlDataFeedService.loadUnrecognizedDataFeed()
        ];
        // Wait for all promises to resolve
        const promiseResults: [PromiseSettledResult<ETLDataFeedObj[]>, PromiseSettledResult<ETLDataFeedObj[]>] =
            await Promise.allSettled(promises);
        // filter all rejected promises and show error.
        promiseResults.filter(p => p.status === 'rejected').map(() => {
            this.toastrManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        });
        // filter all fulfilled promises and flatten all response.
        this.dataFeedGridData = promiseResults.filter(p => p.status === 'fulfilled')
            .flatMap((val: PromiseFulfilledResult<ETLDataFeedObj[]>) => val.value);
        // This is the actual filtered data which is to be displayed
        this.dataFeedGridData.forEach((dataFeedObj) => {
            if (dataFeedObj && dataFeedObj._id.includes('unrecognized')) {
                dataFeedObj.SourceSystemOrig = dataFeedObj.SourceSystem;
                dataFeedObj.SourceSystem = 'Unrecognized';
            }
            return dataFeedObj;
        });
        this.filteredDataFeedGridData = [...this.dataFeedGridData];
        this.gridApi.setRowData(this.filteredDataFeedGridData);
        this.showSpinner = false;
        this.manageNoRowsOverlay();
        this.gridApi.sizeColumnsToFit();
    }

    public async reactivateDataFeed(dataFeedObj: ETLDataFeedObj) {
        if (dataFeedObj !== null && !dataFeedObj.Status?.IsValid && dataFeedObj.SourceSystem !== 'Unrecognized') {
            this.showSpinner = true;
            try {
                await this.cxoneEtlDataFeedService.reactivateDataFeed(dataFeedObj._id);
                this.toastrManager.showToaster('success',
                    this.getLocalizationString("Reactivate_Success_Msg", "Reactivated successfully."));
                this.loadDataFeed();
            } catch (error) {
                this.toastrManager.showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
                this.showSpinner = false;
            } finally {
                this.searchInput = '';
            }
        }
    }

    public async displayDeleteDataFeed(dataFeedObj: ETLDataFeedObj, popover: NgbPopover) {
        if (dataFeedObj !== null && dataFeedObj.SourceSystem !== 'Unrecognized') {
            const dataFeedDependencies = await this.cxoneEtlDataFeedService.getDataFeedDependencies(dataFeedObj?._id);
            if (dataFeedDependencies?.BaseFeeds?.length > 0 || dataFeedDependencies?.Joins?.length > 0
                || dataFeedDependencies?.MetricAbvs?.length > 0) {
                this.openDeleteModal(dataFeedDependencies);
            } else {
                popover.open();
            }
        }
        this.showSpinner = false;
    }

    /**
     *  This method will open the dialog that will display all the dependencies on the data feed.
     *  It will be opened only when there any more than 0 dependency of either Join, Metrics or Base Feed.
     * @returns void
     */
    public openDeleteModal(dataFeedDependencies: ETLDataFeedDependencies): void {
        this.dialogRef = this._dialogService.open(CxoneEtlDataFeedDependenciesComponent, {
            width: '756px',
            height: '553px',
            styleClass: 'cx-one-ui modal-center',
            data: {
                title: this.getLocalizationString("Delete_Data_Feed", "Delete Data Feed"),
                saveLabel: this.getLocalizationString("Delete", "Delete"),
                cancelLabel: this.getLocalizationString("Cancel", "Cancel"),
                deleteWarning: this.getLocalizationString("Delete_Warning_Msg", "Deleting data feed " +
                    "will impact below reporting sets and metrics."),
                allowClose: true,
                dependencies: dataFeedDependencies,
                onDelete: () => this.deleteDataFeed(),
            }
        });
    }

    /**
     *  This method will be called for deleting any selected data feed.
     *  It will be called from selecting Yes from popover or Delete from
     *  Dependency modal. It will display the toaster message.
     * @returns void
     */
    public async deleteDataFeed() {
        this.showSpinner = true;
        try {
            await this.cxoneEtlDataFeedService.deleteDataFeed(this.selectedDataFeed._id);
            this.toastrManager.showToaster('success',
                this.getLocalizationString("Data_Feed_Deleted_Msg", "Data Feed deleted successfully."));
            this.loadDataFeed();
        } catch (error) {
            this.toastrManager.showToaster('error', error.message);
        } finally {
            this.searchInput = '';
            this.selectedDataFeed = null;
        }
    }

    public setGridOptions() {
        this.dataFeedGridOptions = {
            columnDefs: [
                {
                    field: 'SourceSystem',
                    colId: 'SourceSystem',
                    headerName: this.getLocalizationString("Source", "Source"),
                    rowGroup: true,
                    hide: true,
                    resizable: true,
                    sortable: true,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    field: 'FeedName',
                    colId: 'FeedName',
                    headerName: this.getLocalizationString("Feed_Name", "Feed name"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        iconPath: (data: ETLDataFeedObj) => this.getIconPath(data),
                        showTextPopover: true,
                    },
                    resizable: true,
                    sortable: true,
                    suppressMovable: true
                },
                {
                    colId: 'TotalFields',
                    headerName: this.getLocalizationString("Total_Fields", "Total fields"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: false,
                    },
                    valueGetter: (params: ValueGetterParams) => params.data?.DataList?.length.toString(),
                    resizable: true,
                    sortable: false,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    field: 'isActive',
                    colId: 'isActive',
                    headerName: this.getLocalizationString("Active_Fields", "Active fields"),
                    cellRenderer: 'cxoneETLCustomCellRenderer',
                    cellRendererParams: {
                        showTextPopover: false,
                    },
                    valueGetter: (params: ValueGetterParams) =>
                        params.data?.DataList?.filter((obj: DataList) => obj.isActive)?.length.toString(),
                    resizable: true,
                    sortable: false,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    field: 'Status.LastSuccessDate',
                    colId: 'Status.LastSuccessDate',
                    headerName: this.getLocalizationString("Last_Run_On", "Last run on"),
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    valueGetter: (params: ValueGetterParams) => {
                        if (params.data == null) {
                            return null;
                        } else {
                            return params.data?.Status?.LastSuccessDate ?
                                moment(params.data?.Status?.LastSuccessDate).format('MMM D, YYYY h:mm:ss A') : 'NA';
                        }
                    },
                    resizable: true,
                    sortable: false,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    colId: "lockIconCol",
                    cellRenderer: 'lockIconCellRenderer',
                    cellRendererParams: {popover: this.getLocalizationString("Locked_Metric_Tooltip", "Cannot edit or delete")},
                    width: 48,
                    resizable: false,
                    sortable: false,
                    suppressSizeToFit: true,
                    suppressMovable: true
                },
                {
                    colId: 'contextMenu',
                    type: 'simpleContextMenu',
                    cellClass: 'd-flex justify-content-center',
                    resizable: false,
                    sortable: false,
                    suppressSizeToFit: true,
                    width: 36,
                    suppressMovable: true
                }
            ],
            columnTypes: {
                simpleContextMenu: {
                    cellRendererSelector: (params) => {
                        if (params.data == null) {
                            return null;
                        }
                        else {
                            return {
                                component: 'contextMenuCellRenderer',
                                params
                            };
                        }
                    }
                }
            },
            autoGroupColumnDef: {
                headerName: 'SOURCE',
                headerClass: "group-header-align",
                sort: "asc",
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'cxoneHighlightSearchRenderer'
                },
                minWidth: 55 // Min width of 55, so resizing column to be very small will not allow the padding to overlap with neighbor columns
            },
            groupDefaultExpanded: 1,
            components: {
                CustomNoRowsOverlayComponent,
                contextMenuCellRenderer: DataFeedActionsCellRendererComponent,
                cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent,
                cxoneETLCustomCellRenderer: EtlCustomCellValueRendererComponent,
                lockIconCellRenderer:LockIconCellRendererComponent,
            },
            noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                    this.getLocalizationString("No_Result_Found", "No result found")
                    : this.getLocalizationString("No_Feed_Added", "No feed added yet"),
                noRowsOverlayIconPathFunc: () => {
                    const path = this.searchInput !== '' ?
                        '../App_Themes/CXone/icons/no-results.svg#no-results'
                        : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                    return path;
                }
            },
            context: this,
            rowHeight: 36,
            headerHeight: 44,
            animateRows: true,
            scrollbarWidth: 8,
            icons: {
                groupContracted:
                    `<div class="sortIcons">
                        <svg class="icon-10">
                            <use href="../App_Themes/CXone/icons/arrow-right.svg#arrow-right">
                        </use></svg>
                    </div>`,
                groupExpanded:
                    `<div class="sortIcons">
                        <svg class="icon-10">
                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                        </svg>
                    </div>`
            },
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
            onFilterChanged: () => {
                this.gridApi.redrawRows();
                this.setRowCountLabel();
            },
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: (node) => {
                const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
                return result;
            },
            onCellClicked: async (event) => {
                //check to make sure the click did not happen on a group row, if failed this validation just return
                if (event.node.group == true)
                    return;
                //validate that the column clicked is not the context menu either (no category and no context columns)
                if (event.colDef.colId == 'contextMenu'){
                    return;
                }

                let params: ICellRendererParams = {
                    value: undefined,
                    valueFormatted: '',
                    data: undefined,
                    node: undefined,
                    rowIndex: 0,
                    eGridCell: undefined,
                    eParentOfValue: undefined,
                    registerRowDragger: function(rowDraggerElement: HTMLElement, dragStartPixels?: number, value?: string, suppressVisibilityChange?: boolean): void {
                        throw new Error('Function not implemented.');
                    },
                    api: undefined,
                    columnApi: null,
                    context: undefined
                };
                params.data = event.data;

                //check for access to edit
                if(!this.showEditBtn || !event.data.IsEditable){ //if locked or no access
                    //add view logic here
                    this.onRowAction('preview', params, null);
                }
                else{
                    //add edit logic here
                    this.onRowAction('edit', params, null);
                }
            },
            getRowId: ({ data: rowData }) => rowData._id
        };
    }

    public onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridApi.setGridOption('columnDefs', this.dataFeedGridOptions.columnDefs);
        this.gridApi.hideOverlay();
        this.gridApi.showLoadingOverlay();
        this.loadDataFeed();
    }

    public onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
        this.gridApi.onFilterChanged();
        this.manageNoRowsOverlay();
        this.gridApi.sizeColumnsToFit();
    };

    public manageNoRowsOverlay() {
        if (this.gridApi.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi.hideOverlay();
    }

    public getTotalFeedsText(count: number): string {
        const key: string = count === 1 ? 'Total_Feeds_Singular' : 'Total_Feeds_Plural';
        const defaultValue: string = count === 1 ? '{0} feed' : '{0} feeds';
        return this.getLocalizationString(key, defaultValue);
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Data_Feed", key, defaultValue);
        return baseString;
    }

    public setRowCountLabel() {
        let count = 0;
        this.gridApi.forEachNodeAfterFilter((node) => {
            if (!node.group) {
                count++;
            }
        });
        this.dataFeedLabel = this.cvLocalizationService.Format(this.getTotalFeedsText(count), count);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    public onRowAction(action: string = '', params?: ICellRendererParams, popover?: NgbPopover) {
        switch (action) {
            case 'view': {
                if (params.data.Status?.LastSuccessDate && params.data.SourceSystem !== 'Unrecognized') {
                    this.showDataFeedLandingPage = false;
                    this.showDataFeedViewPage = true;
                    this.showFailedImports = false;
                    this.selectedDataFeed = params.data;
                }
                break;
            }
            case 'reactivate': {
                this.showDataFeedLandingPage = true;
                this.showDataFeedViewPage = false;
                this.reactivateDataFeed(params.data);
                break;
            }
            case 'delete': {
                this.showSpinner = true;
                this.showDataFeedLandingPage = true;
                this.showDataFeedViewPage = false;
                this.selectedDataFeed = params.data;
                this.displayDeleteDataFeed(params.data, popover);
                break;
            }
            case 'failed-imports': {
                this.showDataFeedLandingPage = false;
                this.showDataFeedViewPage = false;
                this.showFailedImports = true;
                this.pageTitle = this.getLocalizationString("Data_Feed", "Data Feed");
                this.apiType = 'raw-data';
                break;
            }
            case 'create': {
                this.showDataFeedLandingPage = false;
                this.showCreateNewDataFeedPage = true;
                this.isEditable = false;
                break;
            }
            case 'edit': {
                this.showDataFeedLandingPage = false;
                this.showCreateNewDataFeedPage = true;
                this.isEditable = true;
                this.isPreview = false;
                this.selectedDataFeed = params.data;
                break;
            }
            case 'preview': {
                this.showDataFeedLandingPage = false;
                this.showCreateNewDataFeedPage = true;
                this.isEditable = true;
                this.isPreview = true;
                this.selectedDataFeed = params.data;
                break;
            }
            default: {
                this.showSpinner = true;
                this.showDataFeedLandingPage = true;
                this.showDataFeedViewPage = false;
                this.showFailedImports = false;
                this.showCreateNewDataFeedPage = false;
                this.searchInput = '';
                this.selectedDataFeed = null;
            }
        }
    }

    public getIconPath(data: ETLDataFeedObj): IconDetail {
        if(data?.Status.ConsecutiveFailureCount > 0 && data?.Status.ConsecutiveFailureCount < 10) {
            return {
                iconType: IconType.Failed,
                popoverMsg: this.getLocalizationString("Feed_Last_Run_Failed", "Feed last run failed")
            };
        } else if(data?.Status.ConsecutiveFailureCount >= 10) {
            return {
                iconType: IconType.Inactive,
                popoverMsg: this.getLocalizationString("Data_Feed_Locked_Msg",
                    "This data feed is locked. Reactivate the data definition to import data.")
            };
        } else {
            return {
                iconType: IconType.None
            };
        }
    }
}
